import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import CloseIcon from '@material-ui/icons/Close'
import Autocomplete from '@material-ui/lab/Autocomplete'
import dayjs from 'dayjs'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import {
  fetchNewParentFormOptions,
  searchNewParentFormSchools,
  submitNewParentForm
} from 'api'
import ohLogo from 'assets/images/openhouse_logo.png'

/**
 * THE PARENT'S PAGE — the self-serve alternative to Visitor Entry. A parent
 * scans the QR at a centre's front desk and fills this in on their own phone;
 * no login. It lands exactly where a Visitor Entry does: a walk-in parent with
 * their children in Alfred, and a Walk-in ticket on that centre's Grow & Retain.
 *
 * Each centre has its own link, /new-parent-form?centre=<code>, so the centre
 * is pre-filled from the QR the parent scanned. It stays editable: the code is
 * a convenience, not the authorisation (there is none — see
 * PublicParentFormSubmitAPI for what guards the endpoint).
 */
const OH_ORANGE = '#f25e35'
const MAX_CHILDREN = 6

const emptyChild = () => ({
  first_name: '',
  last_name: '',
  date_of_birth: '',
  standard: '',
  // Once the parent picks a grade, a later date-of-birth change must not
  // overwrite it.
  standardTouched: false,
  school: null, // {uuid, name, area} from the list
  notListed: false,
  school_name: '', // typed when the school isn't in the list
  noSchool: false
})

const ageOn = (dob) => dayjs().diff(dayjs(dob), 'year')

const childSchoolDone = (c) =>
  c.noSchool || (c.notListed ? !!c.school_name.trim() : !!c.school)

// Type-ahead over the schools we already have, with a way out when the
// parent's school isn't one of them.
const SchoolPicker = ({ child, onChange, classes }) => {
  const [input, setInput] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const timer = useRef(null)

  useEffect(() => {
    clearTimeout(timer.current)
    const q = input.trim()
    if (q.length < 3 || (child.school && child.school.name === input)) {
      setResults([])
      return undefined
    }
    timer.current = setTimeout(async () => {
      setLoading(true)
      try {
        const data = await searchNewParentFormSchools(q)
        setResults(data?.results || [])
      } catch (e) {
        setResults([])
      } finally {
        setLoading(false)
      }
    }, 300)
    return () => clearTimeout(timer.current)
  }, [input])

  if (child.noSchool) return null

  if (child.notListed) {
    return (
      <>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="School name"
          value={child.school_name}
          onChange={(e) => onChange({ school_name: e.target.value })}
        />
        <Button
          className={classes.linkBtn}
          onClick={() => onChange({ notListed: false, school_name: '' })}
        >
          Search the list instead
        </Button>
      </>
    )
  }

  return (
    <>
      <Autocomplete
        options={results}
        value={child.school}
        onChange={(_, value) => onChange({ school: value })}
        inputValue={input}
        onInputChange={(_, value) => setInput(value)}
        getOptionLabel={(o) => o?.name || ''}
        getOptionSelected={(o, v) => o.uuid === v.uuid}
        filterOptions={(x) => x}
        loading={loading}
        noOptionsText={
          input.trim().length < 3
            ? 'Type at least 3 letters'
            : "No match — tap “My school isn't listed” below"
        }
        renderOption={(o) => (
          <Box>
            <Typography style={{ fontSize: 14 }}>{o.name}</Typography>
            {o.area && (
              <Typography style={{ fontSize: 12, color: '#8a837a' }}>
                {o.area}
              </Typography>
            )}
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            size="small"
            placeholder="Start typing the school's name"
          />
        )}
      />
      <Button
        className={classes.linkBtn}
        onClick={() => onChange({ notListed: true, school: null })}
      >
        My school isn&apos;t listed
      </Button>
    </>
  )
}

const useStyles = makeStyles((theme) => ({
  page: {
    minHeight: '100vh',
    background: '#fdf6f2',
    padding: theme.spacing(2, 1.5, 6),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    fontFamily: 'Avenir, sans-serif'
  },
  stack: { width: '100%', maxWidth: 560 },
  card: {
    background: '#fff',
    borderRadius: 12,
    border: '1px solid #f0e2da',
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(1.75)
  },
  headerCard: {
    background: '#fff',
    borderRadius: 12,
    border: '1px solid #f0e2da',
    borderTop: `8px solid ${OH_ORANGE}`,
    padding: theme.spacing(3, 2.5, 2.5),
    marginBottom: theme.spacing(1.75)
  },
  logo: { height: 26, display: 'block' },
  title: {
    marginTop: theme.spacing(2),
    fontFamily: 'AvenirDemi, Avenir, sans-serif',
    fontSize: 22,
    color: '#26211b'
  },
  desc: { marginTop: theme.spacing(1), color: '#5f5a52', lineHeight: 1.5 },
  sectionTitle: {
    fontFamily: 'AvenirDemi, Avenir, sans-serif',
    fontSize: 16,
    color: '#26211b',
    marginBottom: theme.spacing(1.5)
  },
  question: {
    fontSize: 15,
    fontWeight: 600,
    color: '#26211b',
    marginBottom: theme.spacing(1)
  },
  field: { marginBottom: theme.spacing(2) },
  req: { color: '#d93025', marginLeft: 2 },
  childBox: {
    border: '1px solid #f0e2da',
    borderRadius: 10,
    padding: theme.spacing(1.5, 1.5, 0.5),
    marginBottom: theme.spacing(1.5),
    position: 'relative'
  },
  childLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: OH_ORANGE,
    marginBottom: theme.spacing(1)
  },
  removeChild: { position: 'absolute', top: 4, right: 4 },
  addChild: { color: OH_ORANGE, textTransform: 'none', fontWeight: 600 },
  linkBtn: {
    color: OH_ORANGE,
    textTransform: 'none',
    fontSize: 13,
    padding: theme.spacing(0.5, 0),
    minWidth: 0
  },
  nameRow: { display: 'flex', gap: theme.spacing(1.5) },
  nameCol: { flex: 1, minWidth: 0 },
  checkbox: { color: OH_ORANGE, '&$checked': { color: OH_ORANGE } },
  checked: {},
  error: {
    background: '#fdecea',
    color: '#8a1c12',
    borderRadius: 8,
    padding: theme.spacing(1.25, 1.5),
    marginBottom: theme.spacing(1.75),
    lineHeight: 1.5
  },
  submitBtn: {
    background: OH_ORANGE,
    color: '#fff',
    fontWeight: 600,
    borderRadius: 50,
    padding: theme.spacing(1.25, 4),
    textTransform: 'none',
    fontSize: 16,
    width: '100%',
    '&:hover': { background: '#d94a23' },
    '&:disabled': { background: '#f2c9bb', color: '#fff' }
  },
  centered: { textAlign: 'center', padding: theme.spacing(6) }
}))

const NewParentForm = () => {
  const classes = useStyles()
  const { search } = useLocation()
  const centreCode = new URLSearchParams(search).get('centre') || ''

  const [options, setOptions] = useState(null)
  const [loadError, setLoadError] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)

  const [centre, setCentre] = useState('')
  const [phone, setPhone] = useState('')
  const [parentFirst, setParentFirst] = useState('')
  const [parentLast, setParentLast] = useState('')
  const [children, setChildren] = useState([emptyChild()])
  const [purposes, setPurposes] = useState([])
  const [awareness, setAwareness] = useState('')

  useEffect(() => {
    fetchNewParentFormOptions()
      .then((data) => {
        setOptions(data)
        // An unknown or stale code just leaves the centre for the parent to pick.
        const match = (data?.centres || []).find(
          (c) => c.code === centreCode.toLowerCase()
        )
        if (match) setCentre(match.uuid)
      })
      .catch(() =>
        setLoadError(
          'We could not load this form. Please check your connection and try again.'
        )
      )
  }, [centreCode])

  const today = dayjs().format('YYYY-MM-DD')
  const phoneValid = /^[6-9][0-9]{9}$/.test(phone)

  const canSubmit = useMemo(
    () =>
      !!centre &&
      phoneValid &&
      !!parentFirst.trim() &&
      !!parentLast.trim() &&
      children.every(
        (c) =>
          !!c.first_name.trim() &&
          !!c.last_name.trim() &&
          !!c.date_of_birth &&
          c.date_of_birth <= today &&
          !!c.standard &&
          childSchoolDone(c)
      ) &&
      purposes.length > 0 &&
      !!awareness,
    [
      centre,
      phoneValid,
      parentFirst,
      parentLast,
      children,
      purposes,
      awareness,
      today
    ]
  )

  // Capture the value NOW — React 16 pools the SyntheticEvent and nulls its
  // target once the handler returns, so reading it inside the updater crashes.
  const updateChild = (index, patch) =>
    setChildren((list) =>
      list.map((c, i) => (i === index ? { ...c, ...patch } : c))
    )

  const setChildField = (index, key) => (e) => {
    const { value } = e.target
    updateChild(index, { [key]: value })
  }

  // The grade follows the date of birth until the parent picks one themselves.
  const setChildDob = (index) => (e) => {
    const { value } = e.target
    setChildren((list) =>
      list.map((c, i) => {
        if (i !== index) return c
        const next = { ...c, date_of_birth: value }
        if (!c.standardTouched && value) {
          const match = options.standards.find((s) => s.age === ageOn(value))
          next.standard = match ? match.uuid : ''
        }
        return next
      })
    )
  }

  const togglePurpose = (item) =>
    setPurposes((list) =>
      list.includes(item) ? list.filter((p) => p !== item) : [...list, item]
    )

  const reset = () => {
    setPhone('')
    setParentFirst('')
    setParentLast('')
    setChildren([emptyChild()])
    setPurposes([])
    setAwareness('')
    setError('')
    setDone(false)
  }

  const submit = async () => {
    if (!canSubmit || saving) return
    setSaving(true)
    setError('')
    try {
      await submitNewParentForm({
        centre,
        phone_number: phone,
        parent_first_name: parentFirst.trim(),
        parent_last_name: parentLast.trim(),
        children: children.map((c) => ({
          first_name: c.first_name.trim(),
          last_name: c.last_name.trim(),
          date_of_birth: c.date_of_birth,
          standard: c.standard,
          school: !c.noSchool && !c.notListed ? c.school?.uuid : null,
          school_name: !c.noSchool && c.notListed ? c.school_name.trim() : '',
          does_not_attend_school: c.noSchool
        })),
        visit_purposes: purposes,
        awareness_source: awareness
      })
      setDone(true)
      window.scrollTo(0, 0)
    } catch (e) {
      const status = e?.response?.status
      if (status === 422) {
        setError(e.response.data?.detail || e.message)
      } else if (status === 429) {
        setError(
          'Too many forms have been sent from this network. Please ask the front desk to help you.'
        )
      } else if (status === 400) {
        setError(e.message)
      } else {
        setError('Could not submit. Please try again.')
      }
    } finally {
      setSaving(false)
    }
  }

  const Req = () => <span className={classes.req}>*</span>

  const header = (
    <Box className={classes.headerCard}>
      <img src={ohLogo} alt="Openhouse" className={classes.logo} />
      <Typography className={classes.title}>Welcome to Openhouse!</Typography>
      <Typography className={classes.desc}>
        Tell us a little about you and your children so our team can get to know
        you.
      </Typography>
    </Box>
  )

  if (loadError) {
    return (
      <Box className={classes.page}>
        <Box className={classes.stack}>
          {header}
          <Box className={classes.error}>{loadError}</Box>
        </Box>
      </Box>
    )
  }

  if (!options) {
    return (
      <Box className={classes.page}>
        <Box className={classes.centered}>
          <CircularProgress style={{ color: OH_ORANGE }} />
        </Box>
      </Box>
    )
  }

  if (done) {
    const centreName = options.centres.find((c) => c.uuid === centre)?.name
    return (
      <Box className={classes.page}>
        <Box className={classes.stack}>
          <Box className={classes.headerCard}>
            <img src={ohLogo} alt="Openhouse" className={classes.logo} />
            <Box
              display="flex"
              alignItems="center"
              style={{ gap: 8, marginTop: 16 }}
            >
              <CheckCircleIcon style={{ color: '#2e7d32' }} />
              <Typography className={classes.title} style={{ marginTop: 0 }}>
                Thank you, {parentFirst.trim()}!
              </Typography>
            </Box>
            <Typography className={classes.desc}>
              We have your details. The Openhouse
              {centreName ? ` ${centreName}` : ''} team will be with you
              shortly.
            </Typography>
          </Box>
          <Button className={classes.addChild} onClick={reset}>
            Fill the form for another family
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <Box className={classes.page}>
      <Box className={classes.stack}>
        {header}

        {/* Parent */}
        <Box className={classes.card}>
          <Typography className={classes.sectionTitle}>About you</Typography>
          <Box className={classes.nameRow}>
            <Box className={classes.nameCol}>
              <Typography className={classes.question}>
                First name
                <Req />
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                className={classes.field}
                value={parentFirst}
                onChange={(e) => setParentFirst(e.target.value)}
                autoComplete="given-name"
              />
            </Box>
            <Box className={classes.nameCol}>
              <Typography className={classes.question}>
                Last name
                <Req />
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                className={classes.field}
                value={parentLast}
                onChange={(e) => setParentLast(e.target.value)}
                autoComplete="family-name"
              />
            </Box>
          </Box>
          <Typography className={classes.question}>
            Mobile number
            <Req />
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="10-digit mobile number"
            value={phone}
            onChange={(e) => {
              // A pasted +91 / 0 prefix is dropped; otherwise stop at 10 digits.
              const digits = e.target.value.replace(/\D/g, '')
              setPhone(
                digits.length > 10 && /^(91|0)/.test(digits)
                  ? digits.slice(-10)
                  : digits.slice(0, 10)
              )
            }}
            inputProps={{ inputMode: 'numeric' }}
            autoComplete="tel-national"
            error={phone.length === 10 && !phoneValid}
            helperText={
              phone.length === 10 && !phoneValid
                ? 'Please enter a valid mobile number.'
                : ''
            }
          />
        </Box>

        {/* Children */}
        <Box className={classes.card}>
          <Typography className={classes.sectionTitle}>
            Your {children.length > 1 ? 'children' : 'child'}
          </Typography>
          {children.map((child, index) => (
            <Box key={index} className={classes.childBox}>
              <Typography className={classes.childLabel}>
                Child {index + 1}
              </Typography>
              {children.length > 1 && (
                <IconButton
                  size="small"
                  className={classes.removeChild}
                  aria-label={`Remove child ${index + 1}`}
                  onClick={() =>
                    setChildren((list) => list.filter((_, i) => i !== index))
                  }
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
              <Box className={classes.nameRow}>
                <Box className={classes.nameCol}>
                  <Typography className={classes.question}>
                    First name
                    <Req />
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    className={classes.field}
                    value={child.first_name}
                    onChange={setChildField(index, 'first_name')}
                  />
                </Box>
                <Box className={classes.nameCol}>
                  <Typography className={classes.question}>
                    Last name
                    <Req />
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    className={classes.field}
                    value={child.last_name}
                    onChange={setChildField(index, 'last_name')}
                  />
                </Box>
              </Box>
              <Typography className={classes.question}>
                Date of birth
                <Req />
              </Typography>
              <TextField
                fullWidth
                type="date"
                variant="outlined"
                size="small"
                className={classes.field}
                value={child.date_of_birth}
                onChange={setChildDob(index)}
                inputProps={{ max: today }}
              />
              <Typography className={classes.question}>
                Grade
                <Req />
              </Typography>
              <TextField
                select
                fullWidth
                variant="outlined"
                size="small"
                className={classes.field}
                value={child.standard}
                onChange={(e) => {
                  const { value } = e.target
                  updateChild(index, { standard: value, standardTouched: true })
                }}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="" disabled>
                  Pick a grade
                </MenuItem>
                {options.standards.map((s) => (
                  <MenuItem key={s.uuid} value={s.uuid}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>
              <Typography className={classes.question}>
                School
                <Req />
              </Typography>
              <SchoolPicker
                child={child}
                classes={classes}
                onChange={(patch) => updateChild(index, patch)}
              />
              <FormControlLabel
                style={{ display: 'flex', marginBottom: 8 }}
                control={
                  <Checkbox
                    checked={child.noSchool}
                    onChange={(e) => {
                      const { checked } = e.target
                      updateChild(index, { noSchool: checked })
                    }}
                    classes={{
                      root: classes.checkbox,
                      checked: classes.checked
                    }}
                  />
                }
                label="Doesn't go to school yet"
              />
            </Box>
          ))}
          {children.length < MAX_CHILDREN && (
            <Button
              className={classes.addChild}
              startIcon={<AddIcon />}
              onClick={() => setChildren((list) => [...list, emptyChild()])}
            >
              Add another child
            </Button>
          )}
        </Box>

        {/* Centre */}
        <Box className={classes.card}>
          <Typography className={classes.question}>
            Which centre are you visiting?
            <Req />
          </Typography>
          <TextField
            select
            fullWidth
            variant="outlined"
            size="small"
            value={centre}
            onChange={(e) => setCentre(e.target.value)}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="" disabled>
              Pick a centre
            </MenuItem>
            {options.centres.map((c) => (
              <MenuItem key={c.uuid} value={c.uuid}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {/* Purpose of visit */}
        <Box className={classes.card}>
          <Typography className={classes.question}>
            What brings you to Openhouse today?
            <Req />
          </Typography>
          {options.visit_purposes.map((item) => (
            <FormControlLabel
              key={item}
              style={{ display: 'flex' }}
              control={
                <Checkbox
                  checked={purposes.includes(item)}
                  onChange={() => togglePurpose(item)}
                  classes={{ root: classes.checkbox, checked: classes.checked }}
                />
              }
              label={item}
            />
          ))}
        </Box>

        {/* Awareness source */}
        <Box className={classes.card}>
          <Typography className={classes.question}>
            How did you hear about us?
            <Req />
          </Typography>
          <RadioGroup
            value={awareness}
            onChange={(e) => setAwareness(e.target.value)}
          >
            {options.awareness_sources.map((item) => (
              <FormControlLabel
                key={item}
                value={item}
                control={
                  <Radio
                    classes={{
                      root: classes.checkbox,
                      checked: classes.checked
                    }}
                  />
                }
                label={item}
              />
            ))}
          </RadioGroup>
        </Box>

        {error && <Box className={classes.error}>{error}</Box>}

        <Button
          className={classes.submitBtn}
          disabled={!canSubmit || saving}
          onClick={submit}
        >
          {saving ? (
            <CircularProgress size={22} style={{ color: '#fff' }} />
          ) : (
            'Submit'
          )}
        </Button>
      </Box>
    </Box>
  )
}

export default NewParentForm
